import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export interface AuditLogPaginationProps {
  pageSize: number;
  count: number;
  pageIndex: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
}

export const AuditLogPagination = ({
  pageSize,
  count,
  pageIndex,
  hasNextPage,
  hasPreviousPage,
  isFetching = false,
  isError = false,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
  className,
}: AuditLogPaginationProps) => {
  const resolvedOptions: readonly number[] = PAGE_SIZE_OPTIONS.includes(pageSize as any)
    ? PAGE_SIZE_OPTIONS
    : ([...PAGE_SIZE_OPTIONS, pageSize].sort((a, b) => a - b) as unknown as readonly number[]);

  const pagerDisabled = isFetching || isError;

  const countLabel = isError
    ? "Couldn't load entries"
    : isFetching
      ? 'Loading entries…'
      : count === 0
        ? 'No entries on this page'
        : `Showing ${count} ${count === 1 ? 'entry' : 'entries'} on page ${pageIndex + 1}`;

  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-neutral-200 px-4 py-3 text-sm text-muted-foreground',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-neutral-600">
          Rows per page
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger
              className="h-8 w-[4.5rem] border-neutral-300 text-xs font-medium text-foreground"
              aria-label="Rows per page"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {resolvedOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <span
          id="footerCount"
          data-testid="footer-count"
          className="text-xs text-neutral-600"
        >
          {countLabel}
        </span>
      </div>

      <div
        className={cn(
          'flex items-center gap-1',
          pagerDisabled && 'pointer-events-none opacity-40'
        )}
      >
        <Button
          id="prevPageBtn"
          data-testid="audit-prev-page-btn"
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 px-3 text-xs font-medium border-neutral-300 text-neutral-700"
          onClick={onPreviousPage}
          disabled={!hasPreviousPage || pagerDisabled}
          aria-label="Go to previous page"
        >
          <CaretLeftIcon className="size-3.5" />
          Previous
        </Button>

        <Button
          id="nextPageBtn"
          data-testid="audit-next-page-btn"
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 px-3 text-xs font-medium border-neutral-300 text-neutral-700"
          onClick={onNextPage}
          disabled={!hasNextPage || pagerDisabled}
          aria-label="Go to next page"
        >
          Next
          <CaretRightIcon className="size-3.5" />
        </Button>
      </div>
    </div>
  );
};
